import React, { useState, useEffect } from 'react';
import '../../styles/wizard.css';
import ImageCropperModal from '../ImageCropperModal';
import { logSaasEvent } from '../../utils/saasAnalytics';
import { useCustomDialog } from '../../context/DialogContext';

const Wizard = () => {
    const { alert: customAlert } = useCustomDialog();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [invalidFields, setInvalidFields] = useState({});

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    useEffect(() => {
        logSaasEvent('signup_step_1_view');
    }, []);

    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [agreedToMarketing, setAgreedToMarketing] = useState(false);

    // Form Data State
    const [formData, setFormData] = useState({
        restaurantName: '',
        ownerName: '',
        username: '',
        menu_slug: '',
        email: '',
        password: '',
        ownerPhone: '',
        restaurantPhone: '',
        restaurantAddress: '',
        state_id: '',
        city_id: '',
        custom_city: '',
        logoFile: null,
        referral_code: ''
    });



    useEffect(() => {
        fetch('https://menu.searchisend.in/apimenu/get_locations.php?action=get_states')
            .then(res => res.json())
            .then(data => { if (data.success) setStates(data.states); })
            .catch(err => console.error(err));
    }, []);

    const fetchCities = (stateId) => {
        if (!stateId) { setCities([]); return; }
        fetch(`https://menu.searchisend.in/apimenu/get_locations.php?action=get_cities&state_id=${stateId}`)
            .then(res => res.json())
            .then(data => { if (data.success) setCities(data.cities); })
            .catch(err => console.error(err));
    };


    const handleChange = (e) => {
        const { name, value, files } = e.target;

        // Clear field validation state when user starts typing/editing
        if (invalidFields[name]) {
            setInvalidFields(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }

        if (name === 'logoFile') {
            if (files && files.length > 0) {
                const file = files[0];
                if (!file.type.startsWith('image/')) {
                    setError('Only image files are allowed for the logo.');
                    customAlert('Only image files are allowed for the logo.', 'Invalid File Type', 'warning');
                    e.target.value = '';
                    return;
                }
                if (file.size > 2 * 1024 * 1024) { // 2MB limit
                    setError('Logo image size must be less than 2MB.');
                    customAlert('Logo image size must be less than 2MB.', 'File Too Large', 'warning');
                    e.target.value = '';
                    return;
                }
                setError('');
                setCropImageSrc(URL.createObjectURL(file));
                e.target.value = ''; // allow re-selecting same file
            }
        } else if (name === 'username') {
            const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
            setFormData(prev => ({ ...prev, [name]: sanitized }));
        } else if (name === 'menu_slug') {
            const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
            setFormData(prev => ({ ...prev, [name]: sanitized }));
        } else if (name === 'state_id') {
            setFormData(prev => ({ ...prev, [name]: value, city_id: '', custom_city: '' }));
            fetchCities(value);
        } else if (name === 'city_id') {
            setFormData(prev => ({ ...prev, [name]: value, custom_city: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCropComplete = (croppedBlob) => {
        const file = new File([croppedBlob], 'logo.jpg', { type: 'image/jpeg' });

        // Revoke old object URL if exists to prevent memory leaks
        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
        }

        const previewUrl = URL.createObjectURL(croppedBlob);
        setLogoPreview(previewUrl);

        setFormData(prev => ({ ...prev, logoFile: file }));
        setCropImageSrc(null);
    };

    const handleSubmit = async () => {
        setError('');
        const invalid = {};

        if (!agreedToTerms) {
            setError("You must agree to the Terms of Service and Privacy Policy.");
            customAlert("You must agree to the Terms of Service and Privacy Policy.", "Terms Agreement Required", "warning");
            return;
        }

        // Basic presence validation
        if (!formData.restaurantName) invalid.restaurantName = true;
        if (!formData.ownerName) invalid.ownerName = true;
        if (!formData.username) invalid.username = true;
        if (!formData.menu_slug) invalid.menu_slug = true;
        if (!formData.email) invalid.email = true;
        if (!formData.password) invalid.password = true;
        if (!formData.state_id) invalid.state_id = true;

        if (Object.keys(invalid).length > 0) {
            setInvalidFields(invalid);
            const msg = "Please fill out all required fields marked with an asterisk (*).";
            setError(msg);
            customAlert(msg, "Required Fields Missing", "warning");
            return;
        }

        // Specific pattern validations
        const usernameRegex = /^[a-zA-Z0-9_]+$/;
        if (!usernameRegex.test(formData.username)) {
            invalid.username = true;
            setInvalidFields(invalid);
            const msg = "Username can only contain letters, numbers, and underscores.";
            setError(msg);
            customAlert(msg, "Invalid Username Format", "warning");
            return;
        }

        const slugRegex = /^[a-zA-Z0-9-]+$/;
        if (!slugRegex.test(formData.menu_slug)) {
            invalid.menu_slug = true;
            setInvalidFields(invalid);
            const msg = "Menu link/slug can only contain letters, numbers, and hyphens.";
            setError(msg);
            customAlert(msg, "Invalid Menu Link Format", "warning");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            invalid.email = true;
            setInvalidFields(invalid);
            const msg = "Please enter a valid email address.";
            setError(msg);
            customAlert(msg, "Invalid Email", "warning");
            return;
        }

        const phoneRegex = /^[6-9]\d{9}$/;
        if (formData.ownerPhone && !phoneRegex.test(formData.ownerPhone)) {
            invalid.ownerPhone = true;
            setInvalidFields(invalid);
            const msg = "Please enter a valid 10-digit owner phone number (e.g., 9876543210).";
            setError(msg);
            customAlert(msg, "Invalid Phone Number", "warning");
            return;
        }
        if (formData.restaurantPhone && !phoneRegex.test(formData.restaurantPhone)) {
            invalid.restaurantPhone = true;
            setInvalidFields(invalid);
            const msg = "Please enter a valid 10-digit restaurant phone number (e.g., 9123456789).";
            setError(msg);
            customAlert(msg, "Invalid Phone Number", "warning");
            return;
        }

        if (!formData.city_id) {
            invalid.city_id = true;
            setInvalidFields(invalid);
            const msg = "Please select a city.";
            setError(msg);
            customAlert(msg, "City Selection Required", "warning");
            return;
        }

        if (formData.city_id === 'custom' && !formData.custom_city) {
            invalid.custom_city = true;
            setInvalidFields(invalid);
            const msg = "Please enter your custom city name.";
            setError(msg);
            customAlert(msg, "Custom City Required", "warning");
            return;
        }

        setLoading(true);
        setError('');
        setInvalidFields({});

        try {
            const fd = new FormData();
            fd.append('action', 'register');
            fd.append('restaurant_name', formData.restaurantName);
            fd.append('owner_name', formData.ownerName);
            fd.append('username', formData.username);
            fd.append('menu_slug', formData.menu_slug);
            fd.append('email', formData.email);
            fd.append('password', formData.password);
            fd.append('owner_phone', formData.ownerPhone);
            fd.append('restaurant_phone', formData.restaurantPhone);
            fd.append('restaurant_address', formData.restaurantAddress);
            fd.append('state_id', formData.state_id);
            fd.append('city_id', formData.city_id === 'custom' ? 0 : formData.city_id);
            fd.append('custom_city', formData.city_id === 'custom' ? formData.custom_city : '');
            fd.append('referral_code', formData.referral_code ? formData.referral_code.trim().toUpperCase() : '');
            if (formData.logoFile) {
                fd.append('logo', formData.logoFile);
            }

            const response = await fetch('https://menu.searchisend.in/apimenu/saas_auth.php', {
                method: 'POST',
                body: fd
            });

            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonErr) {
                console.error("Raw response that failed to parse as JSON:", responseText);
                throw new Error("Invalid response format received from server.");
            }

            if (data.success) {
                logSaasEvent('signup_completed');
                setStep(2); // Show confirmation email screen
            } else {
                setError(data.message || 'Registration failed.');
                customAlert(data.message || 'Registration failed.', 'Registration Failed', 'danger');
            }
        } catch (err) {
            const msg = err.message || 'System error connecting to standard API endpoint.';
            setError(msg);
            customAlert(msg, 'System Error', 'danger');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wizard-page">
            <div className="wizard-container">

                {/* Header Progress */}
                {(step === 1 || step === 2) && (
                    <div className="wizard-progress">
                        <div className="progress-line-bg"></div>
                        <div className="progress-line-active" style={{ width: step === 2 ? '100%' : '0%' }}></div>
                        {[1, 2].map(s => (
                            <div key={s} className={`progress-step ${step >= s ? 'active' : ''}`}>
                                {s}
                            </div>
                        ))}
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* STEP 1: IDENTITY */}
                {step === 1 && (
                    <div className="wizard-step">
                        <div className="wizard-header">
                            <h2>Create Your Account</h2>
                            <p>Fill out the details below to get started instantly.</p>
                        </div>

                        <div className="form-row">
                            <div>
                                <label>Restaurant Name *</label>
                                <input type="text" name="restaurantName" className="form-control" style={{ borderColor: invalidFields.restaurantName ? '#ef4444' : '' }} value={formData.restaurantName} onChange={handleChange} placeholder="e.g., Sharma Dhaba" />
                            </div>
                            <div>
                                <label>Owner Name *</label>
                                <input type="text" name="ownerName" className="form-control" style={{ borderColor: invalidFields.ownerName ? '#ef4444' : '' }} value={formData.ownerName} onChange={handleChange} placeholder="e.g., Rahul Sharma" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div>
                                <label>Login Username *</label>
                                <input
                                    type="text"
                                    name="username"
                                    className="form-control"
                                    style={{ borderColor: invalidFields.username ? '#ef4444' : '' }}
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="e.g., sharma_dhaba"
                                />
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                                    Letters, numbers & underscores only. Used for login.
                                </p>
                            </div>
                            <div>
                                <label>Menu Link Slug *</label>
                                <input
                                    type="text"
                                    name="menu_slug"
                                    className="form-control"
                                    style={{ borderColor: invalidFields.menu_slug ? '#ef4444' : '' }}
                                    value={formData.menu_slug}
                                    onChange={handleChange}
                                    placeholder="e.g., sharma-dhaba"
                                />
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                                    Letters, numbers & hyphens only.
                                    {formData.menu_slug && (
                                        <span style={{ display: 'block', color: '#ea580c', marginTop: '2px', fontWeight: '500' }}>
                                            Your menu URL: menu.searchisend.in/{formData.menu_slug}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="form-row">
                            <div>
                                <label>Login Email *</label>
                                <input type="email" name="email" className="form-control" style={{ borderColor: invalidFields.email ? '#ef4444' : '' }} value={formData.email} onChange={handleChange} placeholder="admin@sharmadhaba.com" />
                            </div>
                            <div>
                                <label>Password *</label>
                                <input type="password" name="password" className="form-control" style={{ borderColor: invalidFields.password ? '#ef4444' : '' }} value={formData.password} onChange={handleChange} placeholder="••••••••" />
                            </div>
                        </div>

                        <div className="form-row">
                            <div>
                                <label>Owner Phone</label>
                                <input type="text" name="ownerPhone" className="form-control" style={{ borderColor: invalidFields.ownerPhone ? '#ef4444' : '' }} value={formData.ownerPhone} onChange={handleChange} placeholder="9876543210" />
                            </div>
                            <div>
                                <label>Restaurant Phone</label>
                                <input type="text" name="restaurantPhone" className="form-control" style={{ borderColor: invalidFields.restaurantPhone ? '#ef4444' : '' }} value={formData.restaurantPhone} onChange={handleChange} placeholder="9123456789" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Restaurant Address</label>
                            <input type="text" name="restaurantAddress" className="form-control" value={formData.restaurantAddress} onChange={handleChange} placeholder="e.g., 123 MG Road" />
                        </div>

                        <div className="form-row">
                            <div>
                                <label>State *</label>
                                <select name="state_id" className="form-control" style={{ borderColor: invalidFields.state_id ? '#ef4444' : '' }} value={formData.state_id} onChange={handleChange}>
                                    <option value="">-- Select State --</option>
                                    {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label>City *</label>
                                <select name="city_id" className="form-control" style={{ borderColor: invalidFields.city_id ? '#ef4444' : '' }} value={formData.city_id} onChange={handleChange} disabled={!formData.state_id}>
                                    <option value="">-- Select City --</option>
                                    {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    <option value="custom">Other (Enter manually)</option>
                                </select>
                            </div>
                        </div>

                        {formData.city_id === 'custom' && (
                            <div className="form-group">
                                <label>Enter City Name * (Pending Approval)</label>
                                <input type="text" name="custom_city" className="form-control" style={{ borderColor: invalidFields.custom_city ? '#ef4444' : '' }} value={formData.custom_city} onChange={handleChange} placeholder="Type your city name" />
                            </div>
                        )}

                        <div className="form-group" style={{ marginTop: '15px' }}>
                            <label>Referral / Promo Code (Optional)</label>
                            <input
                                type="text"
                                name="referral_code"
                                className="form-control"
                                style={{ textTransform: 'uppercase' }}
                                value={formData.referral_code}
                                onChange={handleChange}
                                placeholder="e.g., WELCOME50 or AGENT10"
                            />
                        </div>

                        <div className="form-group">
                            <label>Restaurant Logo (Optional)</label>
                            <input type="file" name="logoFile" className="form-control" onChange={handleChange} accept="image/*" />
                            {formData.logoFile && logoPreview && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                                    <img
                                        src={logoPreview}
                                        alt="Logo Preview"
                                        style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--lp-orange)' }}
                                    />
                                    <p style={{ fontSize: '0.85rem', color: '#10b981', margin: 0, fontWeight: 'bold' }}>
                                        ✓ Logo cropped and ready
                                    </p>
                                </div>
                            )}
                        </div>

                        <div style={{ margin: '15px 0', fontSize: '0.9rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '8px' }}>
                                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} />
                                <span>I agree to the Terms of Service & Privacy Policy *</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={agreedToMarketing} onChange={e => setAgreedToMarketing(e.target.checked)} />
                                <span>I agree to receive marketing and promotional emails</span>
                            </label>
                        </div>

                        <button onClick={handleSubmit} disabled={loading} className="wizard-btn wizard-btn-primary" style={{ marginTop: '10px' }}>
                            {loading ? 'Processing...' : 'Create Account'}
                        </button>
                    </div>
                )}

                {/* STEP 2: SUCCESS EMAIL VERIFICATION */}
                {step === 2 && (
                    <div className="wizard-success">
                        <div className="success-icon"><i className="fas fa-envelope-open-text" style={{ fontSize: '3rem', color: '#10b981' }}></i></div>
                        <h2>Check Your Inbox!</h2>
                        <p style={{ marginTop: '10px' }}>Your digital menu has been created. We have sent a verification link to <strong>{formData.email}</strong>.</p>
                        <p style={{ color: '#6b7280', fontSize: '0.9em', marginTop: '5px' }}>Please click the secure link in the email to activate your account and access your dashboard.</p>
                        <br />
                        <button className="wizard-btn wizard-btn-primary" onClick={() => window.location.href = '/login'}>
                            Proceed to Login
                        </button>
                    </div>
                )}

            </div>

            {cropImageSrc && (
                <ImageCropperModal
                    imageSrc={cropImageSrc}
                    onClose={() => setCropImageSrc(null)}
                    onCropComplete={handleCropComplete}
                />
            )}
        </div>
    );
};

export default Wizard;
